'use client';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Tooltip } from '@mui/material';
import React, { useState } from 'react';

import type { JumpLink } from '@/lib/helpers/contentHelpers';

import { JumpLinksSidebar } from './JumpLinksSidebar';

interface FixedSidebarProps {
  sidebarContent?: React.ReactNode;
  showJumpLinks?: boolean;
  jumpLinks: JumpLink[];
  hasSidebarContent: boolean;
}

export function FixedSidebar({
  sidebarContent,
  showJumpLinks = true,
  jumpLinks,
  hasSidebarContent,
}: FixedSidebarProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      sx={(theme) => ({
        position: 'fixed',
        right: '0',
        bottom: theme.spacing(2),
        width: { xs: 0, lg: isExpanded ? '18rem' : '0.4rem' },
        maxHeight: `calc(calc(100vh - 120px) - ${theme.spacing(4)})`,
        paddingLeft: theme.spacing(2),
        overflowY: 'auto',
        overflowX: 'hidden',
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        gap: 3,
        zIndex: 10,
        borderRadius: 3,
        borderBottomRightRadius: isExpanded ? 3 : 0,
        borderTopRightRadius: isExpanded ? 3 : 0,
        backgroundColor: 'purple.700',
      })}
    >
      {/* Side Toggle button */}
      <IconButton
        onClick={toggleSidebar}
        className={isExpanded ? 'expanded' : 'collapsed'}
        sx={(theme) => ({
          position: 'absolute',
          backgroundColor: 'purple.700',
          left: '0rem',
          top: 0,
          bottom: 0,
          borderRadius: 3,
          borderBottomRightRadius: 0,
          borderTopRightRadius: 0,
          boxShadow: 'none',
          padding: 0,
          paddingLeft: 0,
          paddingRight: '2rem',
          color: 'brand.primarySecondary1',
          transition: 'all 0.2s ease',
          zIndex: -1,
          svg: {
            marginInline: '-0.25rem',
            transition: 'all 0.2s ease',
          },
          '&:hover': {
            background: `linear-gradient(90deg, ${theme.palette.brand.primarySecondary1} 0%, ${theme.palette.brand.primarySecondary1} 30%, transparent 100%)`,
            color: 'white',
          },
          '&.collapsed': {
            paddingRight: 0,
            svg: {
              position: 'relative',
              left: `calc(${theme.spacing(1)} * -0.4)`,
              width: '2rem',
              height: '2rem',
            },
            '&:hover': {
              background: theme.palette.brand.primarySecondary1,
            },
          },
        })}
        aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {isExpanded ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>
      <Box
        sx={{
          width: isExpanded ? 'auto' : '0',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          gap: 2,
          zIndex: 1,
        }}
        className="toggleButton"
      >
        {/* Sidebar content */}
        {/* Sidebar header toggle */}
        <Tooltip title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
          <IconButton
            onClick={toggleSidebar}
            sx={(theme) => ({
              position: 'absolute',
              padding: 0,
              right: theme.spacing(0),
              top: theme.spacing(0),
              borderRadius: 2,
              color: 'brand.primarySecondary1',
              transition: 'all 0.2s ease',
              svg: {
                transition: 'all 0.2s ease',
                width: '2rem',
                height: '2rem',
              },
              '&:hover': {
                backgroundColor: 'brand.primarySecondary1',
                color: 'white',
              },
              zIndex: 11,
            })}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isExpanded ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
        {hasSidebarContent && sidebarContent}
        {showJumpLinks && jumpLinks.length > 0 && <JumpLinksSidebar links={jumpLinks} />}
      </Box>
    </Box>
  );
}
